<script setup lang="ts">
import { addMinutes, parseISO } from 'date-fns';
import { ICalendar } from 'datebook';
import { computed } from 'vue';

import { formatDateOrEmpty } from '@/utils/transformer/time';

import ICON_APPLE from '../../assets/icons/apple.svg';
import ICON_GOOGLE from '../../assets/icons/google.svg';
import ICON_YAHOO from '../../assets/icons/yahoo.svg';

interface Props {
  title: string;
  shareUrl: string;
  startDate?: string;
}

const props = withDefaults(defineProps<Props>(), {
  startDate: ''
});

const emit = defineEmits(['close', 'submit']);

const close = () => emit('close');

const isSupportAppleCalendar = (() => {
  // 判斷裝置是否支援 ICS 檔案

  // 若是 Mac 電腦的話，下載的 ICS 檔案可以進行解析，所以皆開放。
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  // 有可能是 Mobile 裝置且是 inApp 時，會遇到部份 APP 不支援解析 ICS，如：Line、IG
  // 所以目前最低限度判斷瀏覽器是否為 Safari 為基準。

  const isSafari = !!navigator.userAgent.match(/Version\/[\d\.]+\.*Safari/);

  return isMac || isSafari;
})();

const calendarEvent = computed(() => {
  const startTime = parseISO(props.startDate);
  return {
    title: props.title,
    description: props.shareUrl,
    start: startTime,
    end: addMinutes(startTime, 60),
  };
});

const addAppleCalendar = () => {
  if (!isSupportAppleCalendar) { return; }
  const appleCalendar = new ICalendar(calendarEvent.value);
  appleCalendar.download();
};

const addGoogleCalendar = () => {
  // https://github.com/jshor/datebook/blob/master/src/GoogleCalendar.ts
  // Used above for consistency
  const googleUrl = 'https://calendar.google.com/calendar/render?';
  const params = {
    action: 'TEMPLATE', // const
    dates: `${formatDateOrEmpty(calendarEvent.value.start, 'yyyyMMdd\'T\'HHmmss') }/${
      formatDateOrEmpty(calendarEvent.value.end, 'yyyyMMdd\'T\'HHmmss')}`,
    text: calendarEvent.value.title,
    details: calendarEvent.value.description,
  };
  const calendarUrl = googleUrl + new URLSearchParams(params);
  window.open(calendarUrl, '_blank');
};

const addYahooCalendar = () => {
  // https://github.com/jshor/datebook/blob/master/src/YahooCalendar.ts
  // Changed from above for timezone issue
  const yahooUrl = 'https://calendar.yahoo.com/?';
  const params = {
    v: '60', // const api version
    title: calendarEvent.value.title,
    desc: calendarEvent.value.description,
    st: formatDateOrEmpty(calendarEvent.value.start, 'yyyyMMdd\'T\'HHmmss'), // start time
    dur: '0100', // 1 hour in hhmm format
  };
  const calendarUrl = yahooUrl + new URLSearchParams(params);
  window.open(calendarUrl, '_blank');
};
</script>

<template>
<el-dialog
  width="355px"
  custom-class="calendar-dialog"
  :modelValue="true"
  :show-close="true"
  :close-on-click-modal="false"
  :close-on-press-escape="false"
  @close="close"
>
  <template #title>
    <div class="calendar-dialog__header">
      <div class="calendar-dialog__title">{{ $t("cms_setting.calendar.title") }}</div>
    </div>
  </template>

  <div class="calendar-dialog__body">
    <div
      class="calendar-dialog__item"
      v-if="isSupportAppleCalendar"
      @click="addAppleCalendar"
    >
      <img class="calendar-dialog__item__icon" :src="ICON_APPLE"/>
      <div class="calendar-dialog__item__name">{{ $t("cms_setting.calendar.add.apple") }}</div>
    </div>

    <div class="calendar-dialog__item" @click="addYahooCalendar">
      <img class="calendar-dialog__item__icon" :src="ICON_YAHOO"/>
      <div class="calendar-dialog__item__name">{{ $t("cms_setting.calendar.add.yahoo") }}</div>
    </div>

    <div class="calendar-dialog__item" @click="addGoogleCalendar">
      <img class="calendar-dialog__item__icon" :src="ICON_GOOGLE"/>
      <div class="calendar-dialog__item__name">{{ $t("cms_setting.calendar.add.google") }}</div>
    </div>
  </div>
</el-dialog>
</template>
<style lang="scss" scoped>
.calendar-dialog {
  &__title {
    @apply text-lg text-center text-black;
  }

  &__body {
    @apply grid gap-x-8 mx-auto grid-flow-col justify-center;
  }

  &__item {
    @apply cursor-pointer;

    -webkit-tap-highlight-color: transparent;

    &__icon {
      @apply w-10 h-10 mb-1;
    }

    &__name {
      @apply text-xs text-gray-900 text-center;
    }
  }
}
</style>
